import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Contact = ({ onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: "",
  });

  const contactInfo = [
    {
      icon: Phone,
      label: "Phone",
      value: "646-302-2901",
      href: "tel:+16463022901",
    },
    {
      icon: Mail,
      label: "Email",
      value: "info@wealthconnexs.com",
      href: "mailto:info@wealthconnexs.com",
    },
    {
      icon: MapPin,
      label: "Service Area",
      value: "Canada & USA",
      href: null,
    },
    {
      icon: Clock,
      label: "Hours",
      value: "Mon-Fri: 9AM-6PM",
      href: null,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, service_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const success = await onSubmit(formData);
    setIsSubmitting(false);
    if (success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        message: "",
      });
    }
  };

  return (
    <section
      id="contact"
      className="py-24 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="gold-accent mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-4 font-serif">
            Get Your Free Consultation
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Take the first step toward securing your financial future. Our
            experts are ready to help you find the right solution.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="bg-slate-50 rounded-2xl p-8 h-full">
              <h3 className="text-xl font-bold text-navy mb-6 font-serif">
                Contact Information
              </h3>

              <div className="space-y-6 mb-8">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                    className="flex items-center gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">{item.label}</div>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="font-medium text-navy hover:text-amber-600 transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <div className="font-medium text-navy">{item.value}</div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Canada Availability Note */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-navy">Serving Families</div>
                    <div className="text-sm text-slate-600">
                      Available across Canada & USA
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-slate-100 rounded-2xl p-8 shadow-lg"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Full Name *</Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                    className="h-12 border-slate-200 focus:border-navy"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="john@example.com"
                    className="h-12 border-slate-200 focus:border-navy"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="h-12 border-slate-200 focus:border-navy"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-interest">Service Interest</Label>
                  <Select onValueChange={handleSelectChange} value={formData.service_interest}>
                    <SelectTrigger className="h-12 border-slate-200" data-testid="contact-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="iul">IUL (Indexed Universal Life)</SelectItem>
                      <SelectItem value="annuity">Fixed Indexed Annuity</SelectItem>
                      <SelectItem value="term-life">Term Life Insurance</SelectItem>
                      <SelectItem value="simplified-issue">Simplified Issue Life</SelectItem>
                      <SelectItem value="general">General Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Tell us about your financial goals and how we can help..."
                  className="min-h-[120px] resize-none border-slate-200 focus:border-navy"
                  data-testid="contact-message-textarea"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-navy hover:bg-slate-800 text-white h-14 rounded-full text-lg font-medium transition-all duration-200 hover:shadow-lg"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-slate-500 mt-4">
                We respect your privacy. Your information will never be shared.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
